import { useParams } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';

import { GenericTable } from '@/components/ui/generic-table';

import { useOrganisationTags } from '../api/recipe-tag-queries';
import { RecipeTag } from '../types/recipe-tag-types';
import { makeColumns } from '../utils/make-recipe-tags-settings-table-columns';
import { AddRecipeTagDialog } from './add-recipe-tag-dialog';
import { DeleteRecipeTagDialog } from './delete-recipe-tag-dialog';
import { EditRecipeTagDialog } from './edit-recipe-tag-dialog';

interface RecipeTagSettingsTableProps {
  isAddOpen: boolean;
  setIsAddOpen: (open: boolean) => void;
}

export function RecipeTagSettingsTable({
  isAddOpen,
  setIsAddOpen,
}: RecipeTagSettingsTableProps) {
  const { tenantId } = useParams({
    from: '/_authenticated/$tenantId/settings/recipe-tags',
  });

  const { data: tags } = useOrganisationTags(tenantId);
  const [deletingTag, setDeletingTag] = useState<RecipeTag | null>(null);
  const [editingTag, setEditingTag] = useState<RecipeTag | null>(null);

  const handleEdit = useCallback((tag: RecipeTag) => {
    setEditingTag(tag);
  }, []);

  const handleDelete = useCallback((tag: RecipeTag) => {
    setDeletingTag(tag);
  }, []);

  const columns = useMemo(
    () => makeColumns(handleDelete, handleEdit),
    [handleDelete, handleEdit],
  );

  const table = useReactTable({
    data: tags,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <GenericTable table={table} emptyMessage="No recipe tags found." />

      <AddRecipeTagDialog
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
      />
      <DeleteRecipeTagDialog
        tag={deletingTag}
        onClose={() => setDeletingTag(null)}
      />
      <EditRecipeTagDialog
        tag={editingTag}
        onClose={() => setEditingTag(null)}
      />
    </>
  );
}
